import "./sideBar.scss";
import { NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { uiSliceActions } from "store/uiSlice";
import wordmark from "assets/images/wordmark.png";
import { adminListProductVideos } from "services/productVideos";

import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBurger,
    faCartShopping,
    faCity,
    faCommentDots,
    faFileInvoiceDollar,
    faGear,
    faHome,
    faImages,
    faLayerGroup,
    faMapLocationDot,
    faPaperPlane,
    faPercent,
    faPrint,
    faRectangleAd,
    faSitemap,
    faSquarePlus,
    faStar,
    faTags,
    faTruckFast,
    faCalendarDays,
    faUsers,
    faUtensils,
    faUserShield,
    faVideo,
    faWallet,
    faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import { ROLE_PLURAL_LABELS } from "constants/roleLabels";
import { useGetStatistics } from "hooks/useHome";
import { useGetMessagingHealth } from "hooks/useMessagingHealth";
import { getUnseenComplainsCount } from "services/complains";

/**
 * The dashboard navigation, as data.
 *
 * WHY DATA AND NOT JSX. This was ~380 lines of hand-copied `<div><NavLink>`
 * blocks, and every bug the sidebar had was a copy-paste artifact of that: one
 * icon standing in for three unrelated pages, a permission check omitted on the
 * one page that could lock the fleet out, a role gate referencing a permission
 * no form could grant. Ordering was a matter of moving forty lines. Here an
 * item is one row, the gate is one field, and the order is the order.
 *
 * ICONS ARE ALL FONTAWESOME, deliberately — the PNG assets in `assets/icons`
 * are no longer used here. Two reasons beyond the uneven weight of mixing them:
 * `.active` in sideBar.scss repaints `svg path` to white and the `.dark` block
 * repaints it to `--darkFont`, and neither can touch an `<img>`. A PNG icon
 * therefore stayed dark on the orange active row and went near-invisible in
 * dark mode. Every icon here is also distinct — `checkout.png` used to mark
 * طلبات المطاعم, طلبات السائقين *and* كشوف الحسابات, which left the rows
 * distinguishable only by reading their labels.
 *
 * @typedef {object} NavItem
 * @property {string} to - route path, matching src/App.js
 * @property {string} label - Arabic label
 * @property {import("@fortawesome/fontawesome-svg-core").IconDefinition} icon
 * @property {string} [role] - `manage_*` permission required; super admins bypass it
 * @property {boolean} [ai] - draws the AI pill
 * @property {"complaints"|"productVideos"|"openVendors"|"openDeliveries"|"inProgressOrders"|"activeDeliveryOffers"|"unpaidStatements"|"messagingHealth"} [badge]
 *   - draws a counter
 *
 * @typedef {{ title: string, items: NavItem[] }} NavGroup
 */

/**
 * Does this dashboard user get to see this item?
 *
 * The historical expression was `(!super_admin && roles?.X) || super_admin`,
 * repeated per item; that is just `super_admin || roles.X`.
 *
 * @param {object} adminInfo - `state.auth.userData`
 * @param {NavItem} item
 * @returns {boolean}
 */
export const canAccess = (adminInfo, item) => {
    if (!item.role) {
        return true;
    }
    return Boolean(adminInfo?.super_admin || adminInfo?.roles?.[item.role]);
};

/**
 * Admin navigation, grouped by what the work actually is.
 *
 * Grouped by cadence rather than by subject, because subject-grouping is what
 * produced the previous mess: الاعدادات sat second — under the home link — and
 * الشكاوى last, despite being the only item with a live unread counter.
 *
 * THE POINT OF الوارد. الشكاوى, التقييمات and فيديوهات المنتجات are three
 * moderation queues doing the same job: something arrives, you approve, hide or
 * reject it. Filed by subject they scatter — reviews look like a restaurant
 * page, videos look like a catalogue page — and the fact that they are one
 * worklist disappears. Two of them are more than their labels suggest:
 * التقييمات moderates مطعم, منتج *and* سائق targets, so it was never a
 * restaurants page; فيديوهات المنتجات is a قيد المراجعة / معتمدة / مرفوضة
 * queue, not a media library.
 *
 * Queue badges and live account badges come from different sources.
 * الشكاوى reads a city-scoped API count, backed by MySQL since api#136 — it
 * was Firestore, which could not be scoped by city at all. فيديوهات المنتجات
 * reads `pendingCount` off the list endpoint, which already returns it for the
 * page's own tab — the same figure, so the sidebar and the tab cannot disagree.
 * Both queue counts refresh every 30 seconds and on window focus.
 * المطاعم, السائقين and طلبات المطاعم read city-scoped values from the home
 * statistics endpoint. That endpoint also refreshes every 30 seconds.
 *
 * التقييمات is NOT badged, and that is not an omission. `Review.status` is
 * ENUM("visible","hidden") defaulting to "visible" — a review is published on
 * arrival and moderated only if someone complains. There is no pending state to
 * count, so a badge there would either read 0 forever or invent a queue that
 * does not exist. `ProductVideo.status` defaults to "pending", which is what
 * makes it a real queue.
 *
 * ON THE GATES. Nothing here is `super_admin`-only. The force-update lever is
 * the one control that warrants it, and it is gated inside the settings page
 * (`AppVersions`) and enforced by `requireSuperAdmin` on
 * `PUT /api/app-versions/:app`. Gating the whole الاعدادات route was tried and
 * reverted: the page also holds التنبيه and الاغلاق — the kill switch that
 * stops ordering — so the route gate took an emergency control away from every
 * ordinary admin in order to protect a rarely-touched one.
 *
 * The ungated items — الاضافات, اضافات عامه, ارسال اشعار, سلايدر الصور,
 * بانر إعلاني, مناطق التوصيل, الشكاوى, الاعدادات — are visible to every admin ON
 * PURPOSE. Their routes carry a bare `<ProtectedRoute>` and the API enforces no
 * role on them either, so a gate here would be decoration: it would hide the
 * link while leaving the endpoint open, and it would guess at a policy ("is
 * سلايدر الصور a `manage_products` thing?") that nobody has decided. They want a
 * server-side guard and a product decision, in that order, not a client-side
 * `&&`. مناطق التوصيل is the one worth doing first — `POST /api/area` was
 * defaced once, and a null `areaId` hides an order from most drivers.
 *
 * @type {NavGroup[]}
 */
const ADMIN_GROUPS = [
    {
        title: "المتابعة",
        items: [
            { to: "/", label: "الرئيسيه", icon: faHome },
            {
                to: "/orders",
                label: `طلبات ${ROLE_PLURAL_LABELS.vendor}`,
                icon: faCartShopping,
                role: "manage_orders",
                badge: "inProgressOrders",
            },
            {
                to: "/deliveryOrders",
                label: `طلبات ${ROLE_PLURAL_LABELS.delivery}`,
                icon: faTruckFast,
                role: "manage_orders",
            },
            // Driver shifts (admin#74). Operations, not accounts: a shift
            // decides who gets the new-order alarm and who can take an order.
            {
                to: "/driver-shifts",
                label: "ورديات السائقين",
                icon: faCalendarDays,
                role: "manage_orders",
            },
        ],
    },
    {
        // The three queues. They are the same job — something arrives, you
        // approve, hide or reject it — and they were previously filed in three
        // different groups by subject: complaints under operations, reviews
        // under restaurants, videos under the catalogue. Grouping them by
        // subject hides the fact that they are one worklist.
        title: "الوارد",
        items: [
            { to: "/complains", label: "الشكاوى", icon: faCommentDots, badge: "complaints" },
            { to: "/reviews", label: "التقييمات", icon: faStar, role: "manage_reviews" },
            {
                to: "/product-videos",
                label: "فيديوهات المنتجات",
                icon: faVideo,
                role: "manage_product_videos",
                badge: "productVideos",
            },
        ],
    },
    {
        title: "الحسابات",
        items: [
            {
                to: "/users/admins",
                label: ROLE_PLURAL_LABELS.admin,
                icon: faUserShield,
                role: "manage_users",
            },
            {
                to: "/vendors",
                label: ROLE_PLURAL_LABELS.vendor,
                icon: faUtensils,
                role: "manage_vendors",
                badge: "openVendors",
            },
            {
                to: "/users/deliveries",
                label: ROLE_PLURAL_LABELS.delivery,
                icon: faTruckFast,
                role: "manage_users",
                badge: "openDeliveries",
            },
            {
                to: "/users/customers",
                label: ROLE_PLURAL_LABELS.customer,
                icon: faUsers,
                role: "manage_users",
            },
        ],
    },
    {
        title: "القوائم",
        items: [
            { to: "/products", label: "المنتجات", icon: faBurger, role: "manage_products" },
            {
                to: "/menu-extraction",
                label: "استخراج القوائم",
                icon: faWandMagicSparkles,
                role: "manage_vendors",
                ai: true,
            },
            { to: "/options", label: "الاضافات", icon: faSquarePlus },
            { to: "/groups", label: "اضافات عامه", icon: faTags },
        ],
    },
    {
        title: "المال",
        items: [
            {
                to: "/statements",
                label: "كشوف الحسابات",
                icon: faFileInvoiceDollar,
                role: "manage_orders",
                badge: "unpaidStatements",
            },
        ],
    },
    {
        title: "التسويق",
        items: [
            { to: "/send-notification", label: "ارسال اشعار", icon: faPaperPlane },
            {
                to: "/messaging-health",
                label: "حالة الرسائل",
                icon: faCommentDots,
                badge: "messagingHealth",
            },
            {
                to: "/delivery-offers",
                label: "عروض التوصيل",
                icon: faPercent,
                role: "manage_delivery_offers",
                badge: "activeDeliveryOffers",
            },
            { to: "/sliders", label: "سلايدر الصور", icon: faImages },
            { to: "/banners", label: "بانر إعلاني", icon: faRectangleAd },
        ],
    },
    {
        title: "الإعداد",
        items: [
            { to: "/categories", label: "الاقسام", icon: faLayerGroup, role: "manage_categories" },
            {
                to: "/vendor-categories",
                label: "تصنيف المطاعم",
                icon: faSitemap,
                role: "manage_categories",
            },
            { to: "/cities", label: "المدن", icon: faCity, role: "manage_cities" },
            { to: "/regions", label: "مناطق التوصيل", icon: faMapLocationDot },
            { to: "/settings", label: "الاعدادات", icon: faGear },
        ],
    },
];

/**
 * Vendor navigation — `role === "vendor"` users log into this same dashboard
 * and have no Admin row, so none of the `manage_*` gates apply to them.
 *
 * @type {NavGroup[]}
 */
const VENDOR_GROUPS = [
    {
        title: "التشغيل اليومي",
        items: [
            { to: "/", label: "الرئيسيه", icon: faHome },
            { to: "/orders", label: "الطلبات", icon: faCartShopping },
            { to: "/times", label: "مواعيد العمل", icon: faClock },
            { to: "/auto-print", label: "الطباعة التلقائية", icon: faPrint },
        ],
    },
    {
        title: "قائمتي",
        items: [
            { to: "/products", label: "المنتجات", icon: faBurger },
            { to: "/product-videos", label: "فيديوهاتي", icon: faVideo },
            { to: "/my-reviews", label: "تقييمات المطعم", icon: faStar },
        ],
    },
    {
        title: "المحاسبة",
        items: [
            { to: "/statements", label: "كشوف الحسابات", icon: faFileInvoiceDollar },
            // The restaurant's balance and why it may be closed (admin#41).
            { to: "/wallet", label: "المحفظة", icon: faWallet },
        ],
    },
];

const SideBar = () => {
    const sideBarVisiablity = useSelector((state) => state.ui.sideBarIsVisiable);
    const adminInfo = useSelector((state) => state.auth.userData);

    const dispatch = useDispatch();
    const isAdmin = adminInfo?.role === "admin";
    const homeCityId = isAdmin && !adminInfo?.super_admin ? adminInfo?.cityId : undefined;
    const { data: homeStatistics } = useGetStatistics(homeCityId, { enabled: isAdmin });
    const {
        health: messagingHealth,
        loading: messagingHealthLoading,
        error: messagingHealthError,
    } = useGetMessagingHealth({ enabled: isAdmin });
    const canSeeProductVideos = isAdmin && canAccess(adminInfo, { role: "manage_product_videos" });
    const { data: productVideoStatistics } = useQuery({
        queryKey: ["sidebar-pending-product-videos", homeCityId ?? "all"],
        queryFn: () => adminListProductVideos({ limit: 1, status: "pending" }),
        enabled: canSeeProductVideos,
        refetchInterval: 30_000,
        refetchOnWindowFocus: true,
    });
    const { data: complainStatistics } = useQuery({
        queryKey: ["sidebar-unseen-complaints", homeCityId ?? "all"],
        queryFn: getUnseenComplainsCount,
        enabled: isAdmin,
        refetchInterval: 30_000,
        refetchOnWindowFocus: true,
    });

    const sideBarHandler = () => {
        dispatch(uiSliceActions.toggleSideBar());
    };

    const groups = adminInfo?.role === "vendor" ? VENDOR_GROUPS : isAdmin ? ADMIN_GROUPS : [];

    /** Counts are per-badge-kind and stay exact. */
    const badgeCount = (kind) => {
        if (kind === "complaints") return complainStatistics?.count || 0;
        if (kind === "productVideos") return productVideoStatistics?.pendingCount || 0;
        if (kind === "openVendors") return homeStatistics?.openVendors || 0;
        if (kind === "inProgressOrders") return homeStatistics?.inProgressOrders || 0;
        if (kind === "activeDeliveryOffers") return homeStatistics?.activeDeliveryOffers || 0;
        if (kind === "unpaidStatements") return homeStatistics?.unpaidStatements || 0;
        return 0;
    };

    const badgeLabel = (kind) => {
        if (kind === "messagingHealth") {
            if (messagingHealthLoading) return "جارٍ الفحص";
            if (messagingHealthError || !messagingHealth) return "غير متاح";
            return messagingHealth.isHealthy ? "تعمل" : "مشكلة";
        }

        const count = badgeCount(kind);
        if (count === 0) return null;
        if (kind === "complaints") return `جديدة ${count}`;
        if (kind === "productVideos") return `مراجعة ${count}`;
        if (kind === "openVendors") return `مفتوحة ${count}`;
        if (kind === "inProgressOrders") return `جارية ${count}`;
        if (kind === "activeDeliveryOffers") return `سارية ${count}`;
        if (kind === "unpaidStatements") return `غير مدفوعة ${count}`;
        return null;
    };

    const driverAvailabilityBadge = () => {
        const declaredOnline = homeStatistics?.onlineDeliveries;
        if (typeof declaredOnline !== "number" || declaredOnline <= 0) return null;
        const appOpen = homeStatistics?.appOpenDeliveries;
        const hasAppPresence = typeof appOpen === "number";

        return {
            label: hasAppPresence
                ? `متصل ${declaredOnline} · تطبيق ${appOpen}`
                : `متصل ${declaredOnline}`,
            title: "متصل: أعلن السائق أنه متصل · تطبيق: اتصال حديث من التطبيق",
        };
    };

    const badgeTitle = (kind) => {
        if (kind !== "messagingHealth") return undefined;
        if (messagingHealth?.isHealthy) return "قنوات الرسائل تعمل بشكل طبيعي";
        if (messagingHealthError) return "تعذّر قراءة حالة قنوات الرسائل";
        return "يوجد خلل في قناة رسائل واحدة أو أكثر";
    };

    const renderItem = (item) => (
        <div key={item.to}>
            <NavLink
                // `end` matters only on "/" — without it NavLink v6 marks the
                // home link active on every nested route. `exact`/`activeclassname`
                // used to be passed here; neither exists in v6 (they were v5),
                // so they were landing on the DOM node as unknown attributes
                // while `.active`, which v6 adds by itself, did the actual work.
                end={item.to === "/"}
                to={item.to}
                className="link"
                onClick={sideBarHandler}
            >
                <FontAwesomeIcon icon={item.icon} size="lg" fixedWidth />
                <span>{item.label}</span>
                {item.ai ? <span className="ai-badge">AI</span> : null}
                {/* Rendered inside the link so it is part of the click target. */}
                {item.badge === "openDeliveries" ? (
                    (() => {
                        const badge = driverAvailabilityBadge();
                        return badge ? (
                            <span className="count-badge count-badge--driver" title={badge.title}>
                                {badge.label}
                            </span>
                        ) : null;
                    })()
                ) : item.badge && badgeLabel(item.badge) ? (
                    <span
                        className="count-badge count-badge--labeled"
                        title={badgeTitle(item.badge)}
                    >
                        {badgeLabel(item.badge)}
                    </span>
                ) : null}
            </NavLink>
        </div>
    );

    return (
        <>
            <div
                className={sideBarVisiablity ? "overlay openOverlay" : "overlay"}
                onClick={sideBarHandler}
            ></div>
            <div className={`sidebar ${sideBarVisiablity ? "openSideBar" : ""}`}>
                <div className="logo center">
                    <img className="brand-wordmark" src={wordmark} alt="طلباتك" />
                </div>
                <div className="link-container">
                    <div className="pages">
                        {groups.map((group) => {
                            const items = group.items.filter((item) => canAccess(adminInfo, item));
                            if (items.length === 0) {
                                return null;
                            }
                            return (
                                <div className="nav-group" key={group.title}>
                                    <p className="nav-group-title">{group.title}</p>
                                    {items.map(renderItem)}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};
export default SideBar;
